import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/components/utils/utils';
import { getAvatarColor, getInitials } from '@/components/utils/avatar-colors';

/**
 * Pallino con le iniziali dell'utente, colorato in modo stabile.
 *
 * Il colore nasce dall'hash di `colorSeed`: passare sempre lo stesso valore per la stessa
 * persona (di norma l'email) e' cio' che rende l'avatar riconoscibile fra schermate diverse.
 * Dove l'email non e' disponibile si passa l'id: il colore resta stabile nel tempo ma non
 * coincide con quello mostrato altrove per la stessa persona.
 */
export type UserAvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLASSES: Record<UserAvatarSize, string> = {
    xs: 'h-6 w-6 text-[10px]',
    sm: 'h-7 w-7 text-xs',
    md: 'h-8 w-8 text-sm',
    lg: 'h-10 w-10 text-base',
    /** Testata di pagina (profilo): l'avatar e' l'elemento principale, non un accessorio. */
    xl: 'h-16 w-16 text-xl',
};

/**
 * Versione `expanded`: il cerchio scende di una taglia, perche' accanto c'e' il nome e non
 * deve piu' reggere da solo il riconoscimento.
 */
const EXPANDED_AVATAR_CLASSES: Record<UserAvatarSize, string> = {
    xs: 'h-5 w-5 text-[9px]',
    sm: 'h-6 w-6 text-[10px]',
    md: 'h-7 w-7 text-xs',
    lg: 'h-8 w-8 text-sm',
    xl: 'h-10 w-10 text-base',
};

/** Corpo del nome accanto al cerchio, in scala con la taglia. */
const LABEL_CLASSES: Record<UserAvatarSize, string> = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg',
};

interface UserAvatarProps {
    firstName?: string | null;
    lastName?: string | null;
    /** Alternativa a firstName/lastName quando si ha solo il nome completo. */
    fullName?: string | null;
    email?: string | null;
    /** Sorgente del colore. Default: l'email. */
    colorSeed?: string | null;
    imageUrl?: string | null;
    size?: UserAvatarSize;
    className?: string;
    /** Fallback quando non c'e' nulla da cui ricavare le iniziali. */
    placeholder?: string;
    /**
     * Mostra nome ed email al passaggio del mouse. Serve dove l'avatar sta da solo in una
     * colonna: senza, le iniziali sarebbero un indovinello.
     * Richiede un TooltipProvider a monte, come per gli altri tooltip dell'app.
     */
    showTooltip?: boolean;
    /**
     * Riga extra in coda al tooltip: serve a dire CHI e' questa persona rispetto alla riga
     * ("Cliente", "Advisor", "Sei tu"). Senza, due pallini affiancati sono indistinguibili.
     * Ha effetto solo con `showTooltip`.
     */
    hint?: string;
    /**
     * Versione estesa: lo stesso cerchio, una taglia piu' piccolo, seguito da nome e
     * cognome in chiaro. Da usare dove c'e' spazio e il nome va letto senza passare il
     * mouse; il cerchio resta l'aggancio visivo con la versione compatta.
     */
    expanded?: boolean;
}

/**
 * Iniziali da nome e cognome, con ripiego su nome completo e infine sull'email.
 * Nessun dato utile non deve produrre un pallino vuoto.
 */
const resolveInitials = (
    firstName?: string | null,
    lastName?: string | null,
    fullName?: string | null,
    email?: string | null,
    placeholder = '?'
): string => {
    const fromNames = getInitials(firstName ?? '', lastName ?? '');
    if (fromNames) return fromNames;

    const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return getInitials(parts[0], parts[parts.length - 1]);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

    const localPart = (email ?? '').trim();
    if (localPart) return localPart.slice(0, 2).toUpperCase();

    return placeholder;
};

export const UserAvatar = ({
    firstName,
    lastName,
    fullName,
    email,
    colorSeed,
    imageUrl,
    size = 'md',
    className,
    placeholder,
    showTooltip = false,
    hint,
    expanded = false,
}: UserAvatarProps) => {
    const initials = resolveInitials(firstName, lastName, fullName, email, placeholder);
    const seed = colorSeed ?? email ?? fullName ?? '';
    const displayName =
        fullName?.trim() ||
        [firstName, lastName].filter(Boolean).join(' ').trim() ||
        null;

    const circle = (
        <Avatar
            className={cn(
                'flex-shrink-0',
                expanded ? EXPANDED_AVATAR_CLASSES[size] : SIZE_CLASSES[size],
                !expanded && className
            )}
        >
            {imageUrl && <AvatarImage src={imageUrl} alt={displayName ?? email ?? initials} />}
            <AvatarFallback className={cn('text-white font-medium', getAvatarColor(seed))}>
                {initials}
            </AvatarFallback>
        </Avatar>
    );

    const avatar = expanded ? (
        <span className={cn('flex min-w-0 items-center gap-2', className)}>
            {circle}
            <span className={cn('truncate text-gray-900', LABEL_CLASSES[size])}>
                {displayName ?? email ?? placeholder ?? '—'}
            </span>
        </span>
    ) : (
        circle
    );

    if (!showTooltip || (!displayName && !email && !hint)) return avatar;

    return (
        <Tooltip>
            {/* span intermedio: Avatar non inoltra il ref che TooltipTrigger richiede */}
            <TooltipTrigger asChild>
                <span
                    className={cn('cursor-default', expanded ? 'flex min-w-0' : 'inline-flex')}
                    aria-label={displayName ?? email ?? undefined}
                >
                    {avatar}
                </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-sm font-normal">
                <span className="flex flex-col">
                    {/* In versione estesa il nome e' gia' in chiaro: ripeterlo nel tooltip
                        sarebbe rumore, restano email e ruolo. */}
                    {displayName && !expanded && (
                        <span className="font-medium text-popover-foreground">{displayName}</span>
                    )}
                    {email && <span className="text-xs text-muted-foreground">{email}</span>}
                    {hint && (
                        <span className="mt-0.5 text-xs font-medium text-muted-foreground">{hint}</span>
                    )}
                </span>
            </TooltipContent>
        </Tooltip>
    );
};

export default UserAvatar;
